import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { usePermissions } from "../contexts/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { 
  ChevronLeft, Search, Download, Users, Filter, 
  UserCheck, UserX, Mail, Phone, Building, 
  Edit2, Trash2, Plus, X, Check, Clock,
  FileSpreadsheet, RefreshCw, MessageSquare, Crown, Shield,
  Square, CheckSquare, Settings, Lock, Unlock, Columns, Eye, EyeOff,
  Briefcase, MapPin, Map, AlertCircle, Save
} from "lucide-react";
import { useIsArchived, ArchivedBanner } from "../components/ArchivedBanner";
import PoweredByFooter from "../components/PoweredByFooter";

export default function Frivillige({ user, onLogout, selectedEvent }) {
  const navigate = useNavigate();
  const isArchived = useIsArchived();
  const [volunteers, setVolunteers] = useState([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterForening, setFilterForening] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({ total: 0, bySource: {}, byRole: {}, byForening: {}, admins: 0, leaders: 0 });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVolunteer, setNewVolunteer] = useState({ name: '', email: '', role: 'frivillig', forening: '', hold: '', phone: '', is_leader: false });
  
  // Specialfiltre
  const [filterNoMainRole, setFilterNoMainRole] = useState(false);
  const [filterNoRole, setFilterNoRole] = useState(false);
  const [filterHasNotes, setFilterHasNotes] = useState(false);
  
  // Nye avancerede filtre
  const [filterMainRole, setFilterMainRole] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterWorkZone, setFilterWorkZone] = useState('');
  const [filterShift, setFilterShift] = useState('');
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  
  // Funktioner og Vagter for bulk-tildeling
  const [mainRoles, setMainRoles] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [bulkMainRoleId, setBulkMainRoleId] = useState('');
  const [bulkShiftIds, setBulkShiftIds] = useState([]);
  
  // Bulk låse-indstillinger
  const [bulkMainRoleLocked, setBulkMainRoleLocked] = useState(false);
  const [bulkMainRoleUnlock, setBulkMainRoleUnlock] = useState(false);
  const [bulkAreaId, setBulkAreaId] = useState('');
  const [bulkAreaLocked, setBulkAreaLocked] = useState(false);
  const [bulkAreaUnlock, setBulkAreaUnlock] = useState(false);
  const [bulkWorkZoneId, setBulkWorkZoneId] = useState('');
  const [bulkWorkZoneLocked, setBulkWorkZoneLocked] = useState(false);
  const [bulkWorkZoneUnlock, setBulkWorkZoneUnlock] = useState(false);
  
  // Notes
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteVolunteer, setNoteVolunteer] = useState(null);
  const [notes, setNotes] = useState([]);
  
  // Kolonne-visning
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['name', 'email', 'funktion', 'team', 'is_leader', 'forening', 'source', 'notes']);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Låse-indstillinger modal
  const [showLockSettings, setShowLockSettings] = useState(false);
  const [lockSettingsVolunteer, setLockSettingsVolunteer] = useState(null);
  const [areas, setAreas] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  
  // Rediger-dialog (erstatter inline-redigering)
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Hold-filter for ledere
  const [selectedTeamFilter, setSelectedTeamFilter] = useState(() => {
    // Hent fra localStorage eller 'all'
    const saved = localStorage.getItem(`frivillige_teamFilter_${localStorage.getItem("selectedEventId")}`);
    return saved || 'all';
  });
  
  const token = localStorage.getItem("token");
  const eventId = selectedEvent?.id || localStorage.getItem("selectedEventId");
  const eventName = selectedEvent?.name || localStorage.getItem("selectedEventName");
  const isAdmin = user?.role === 'admin';
  const isLeader = user?.role === 'leader' || user?.role === 'admin';

  // Helper funktion til at vise team-navn (konverterer ID til navn hvis nødvendigt)
  const getTeamName = (teamValue) => {
    if (!teamValue) return null;
    // Tjek om det er et UUID/ID format
    if (teamValue.includes('-') && teamValue.length > 30) {
      // Find team-navn fra roles
      const team = roles.find(r => r.id === teamValue);
      return team?.name || teamValue;
    }
    return teamValue;
  };
  
  // Hent rettigheder fra context
  const { canPerformAction, hasPermission } = usePermissions();
  const canEditVolunteers = isAdmin || hasPermission('frivillige_rediger');
  const canDeleteVolunteers = isAdmin || hasPermission('frivillige_slet');

  const showConfirm = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };
  
  // Gem hold-filter i localStorage når det ændres
  const handleTeamFilterChange = (value) => {
    setSelectedTeamFilter(value);
    localStorage.setItem(`frivillige_teamFilter_${eventId}`, value);
  };
  
  // Åbn rediger-dialog
  const openEditDialog = (volunteer) => {
    setEditingVolunteer({...volunteer});
    setShowEditDialog(true);
  };
  
  // Luk rediger-dialog
  const closeEditDialog = () => {
    setEditingVolunteer(null);
    setShowEditDialog(false);
  };

  // Load data
  const loadVolunteers = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/events/${eventId}/volunteers/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVolunteers(res.data);
      
      // Beregn statistik
      const bySource = {};
      const byRole = {};
      const byForening = {};
      let admins = 0;
      let leaders = 0;
      
      res.data.forEach(v => {
        // Kilde
        const source = v.source || 'unknown';
        bySource[source] = (bySource[source] || 0) + 1;
        
        // Rolle
        const role = v.role || 'Ingen rolle';
        byRole[role] = (byRole[role] || 0) + 1;
        
        // Forening
        const forening = v.forening || 'Ingen forening';
        byForening[forening] = (byForening[forening] || 0) + 1;
        
        // Tæl admins og ledere
        const roleLower = (v.role || '').toLowerCase();
        if (roleLower === 'admin' || roleLower === 'administrator') {
          admins++;
        } else if (v.is_leader || ['leder', 'leader', 'teamleder', 'sektionsleder'].includes(roleLower)) {
          leaders++;
        }
      });
      
      setStats({
        total: res.data.length,
        bySource,
        byRole,
        byForening,
        admins,
        leaders
      });
    } catch (error) {
      console.error("Fejl ved hentning af frivillige:", error);
      toast.error("Kunne ikke hente frivillige");
    } finally {
      setLoading(false);
    }
  }, [eventId, token]);

  const loadRoles = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/roles`);
      setRoles(res.data);
    } catch (error) {
      console.error("Fejl ved hentning af roller:", error);
    }
  }, [eventId]);

  const loadMainRoles = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/main-roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMainRoles(res.data);
    } catch (error) {
      console.error("Fejl ved hentning af funktioner:", error);
    }
  }, [eventId, token]);

  const loadShifts = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${API}/events/${eventId}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShifts(res.data);
    } catch (error) {
      console.error("Fejl ved hentning af vagter:", error);
    }
  }, [eventId, token]);

  const loadAreasAndZones = useCallback(async () => {
    if (!eventId) return;
    try {
      const [areasRes, zonesRes] = await Promise.all([
        axios.get(`${API}/areas?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/work-zones?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAreas(areasRes.data);
      setWorkZones(zonesRes.data);
    } catch (error) {
      console.error("Fejl ved hentning af områder/zoner:", error);
    }
  }, [eventId, token]);

  // Åbn låse-indstillinger for en frivillig
  const openLockSettings = (volunteer) => {
    setLockSettingsVolunteer({
      ...volunteer,
      main_role_id: volunteer.main_role_id || '',
      main_role_locked: volunteer.main_role_locked || false,
      area_id: volunteer.area_id || '',
      area_locked: volunteer.area_locked || false,
      work_zone_id: volunteer.work_zone_id || '',
      work_zone_locked: volunteer.work_zone_locked || false
    });
    setShowLockSettings(true);
  };

  // Gem låse-indstillinger
  const saveLockSettings = async () => {
    if (!lockSettingsVolunteer) return;
    
    try {
      const endpoint = lockSettingsVolunteer.source === 'excel_import'
        ? `${API}/events/${eventId}/imported-volunteers/${lockSettingsVolunteer.id}`
        : `${API}/events/${eventId}/volunteers/${lockSettingsVolunteer.id}`;
      
      await axios.put(endpoint, {
        main_role_id: lockSettingsVolunteer.main_role_id || null,
        main_role_locked: lockSettingsVolunteer.main_role_locked,
        area_id: lockSettingsVolunteer.area_id || null,
        area_locked: lockSettingsVolunteer.area_locked,
        work_zone_id: lockSettingsVolunteer.work_zone_id || null,
        work_zone_locked: lockSettingsVolunteer.work_zone_locked
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Indstillinger gemt");
      setShowLockSettings(false);
      setLockSettingsVolunteer(null);
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved gemning");
    }
  };

  useEffect(() => {
    loadVolunteers();
    loadRoles();
    loadMainRoles();
    loadShifts();
    loadAreasAndZones();
  }, [loadVolunteers, loadRoles, loadMainRoles, loadShifts, loadAreasAndZones]);

  // Filtrering
  useEffect(() => {
    let filtered = [...volunteers];
    
    // Hold-filter for ledere (baseret på funktion)
    if (selectedTeamFilter && selectedTeamFilter !== 'all' && isLeader && !isAdmin) {
      filtered = filtered.filter(v => v.main_role_id === selectedTeamFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(v => 
        v.name?.toLowerCase().includes(term) ||
        v.email?.toLowerCase().includes(term) ||
        v.forening?.toLowerCase().includes(term) ||
        v.hold?.toLowerCase().includes(term)
      );
    }
    
    if (filterRole) {
      filtered = filtered.filter(v => v.role === filterRole);
    }
    
    if (filterSource) {
      filtered = filtered.filter(v => v.source === filterSource);
    }
    
    if (filterForening) {
      filtered = filtered.filter(v => v.forening === filterForening);
    }
    
    // Specialfiltre
    if (filterNoMainRole) {
      filtered = filtered.filter(v => !v.main_role_id);
    }
    
    if (filterNoRole) {
      filtered = filtered.filter(v => !v.role || v.role === 'frivillig');
    }
    
    if (filterHasNotes) {
      filtered = filtered.filter(v => v.has_notes);
    }
    
    // Nye avancerede filtre
    if (filterMainRole) {
      filtered = filtered.filter(v => v.main_role_id === filterMainRole);
    }
    
    if (filterArea) {
      filtered = filtered.filter(v => v.area_id === filterArea);
    }
    
    if (filterWorkZone) {
      filtered = filtered.filter(v => v.work_zone_id === filterWorkZone);
    }
    
    if (filterShift) {
      filtered = filtered.filter(v => v.shift_ids && v.shift_ids.includes(filterShift));
    }
    
    setFilteredVolunteers(filtered);
  }, [volunteers, searchTerm, filterRole, filterSource, filterForening, filterNoMainRole, filterNoRole, filterHasNotes, filterMainRole, filterArea, filterWorkZone, filterShift, selectedTeamFilter, isLeader, isAdmin]);

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredVolunteers.filter(v => v.can_edit !== false).map(v => v.id)));
    }
    setSelectAll(!selectAll);
  };

  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
    setSelectAll(newSet.size === filteredVolunteers.filter(v => v.can_edit !== false).length);
  };

  // Bulk delete
  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    
    showConfirm(
      `Slet ${selectedIds.size} frivillige?`,
      `Er du sikker på du vil slette ${selectedIds.size} markerede frivillige? Denne handling kan ikke fortrydes.`,
      async () => {
        closeConfirm();
        try {
          const res = await axios.post(
            `${API}/events/${eventId}/volunteers/bulk-delete`,
            Array.from(selectedIds),
            { headers: { Authorization: `Bearer ${token}` }}
          );
          toast.success(`Slettet ${res.data.deleted_count} frivillige`);
          setSelectedIds(new Set());
          setSelectAll(false);
          loadVolunteers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
      }
    );
  };

  // Notes functions
  const openNotes = async (volunteer) => {
    setNoteVolunteer(volunteer);
    setShowNoteDialog(true);
    setLoadingNotes(true);
    
    try {
      const res = await axios.get(
        `${API}/events/${eventId}/volunteers/${volunteer.id}/notes`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNotes(res.data);
    } catch (error) {
      toast.error("Kunne ikke hente noter");
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const createNote = async () => {
    if (!newNote.trim() || !noteVolunteer) return;
    
    try {
      await axios.post(
        `${API}/events/${eventId}/volunteers/${noteVolunteer.id}/notes?content=${encodeURIComponent(newNote)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Note oprettet");
      setNewNote('');
      // Reload notes
      const res = await axios.get(
        `${API}/events/${eventId}/volunteers/${noteVolunteer.id}/notes`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNotes(res.data);
      loadVolunteers(); // Reload for at opdatere has_notes
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke oprette note");
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/notes/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setNotes(notes.filter(n => n.id !== noteId));
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke slette note");
    }
  };

  // Export til Excel
  const exportToExcel = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/volunteers/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `frivillige_${eventName}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel-fil downloadet!");
    } catch (error) {
      // Fallback til CSV hvis Excel-endpoint ikke findes
      try {
        const res = await axios.get(`${API}/events/${eventId}/volunteers/export`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', res.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success("CSV-fil downloadet!");
      } catch (err) {
        toast.error("Kunne ikke eksportere data");
      }
    }
  };

  // Tilføj frivillig
  const addVolunteer = async () => {
    if (!newVolunteer.email || !newVolunteer.name) {
      toast.error("Navn og email er påkrævet");
      return;
    }
    
    try {
      await axios.post(`${API}/events/${eventId}/volunteers/add`, newVolunteer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Frivillig tilføjet!");
      setShowAddForm(false);
      setNewVolunteer({ name: '', email: '', role: '', forening: '', hold: '', phone: '' });
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved tilføjelse");
    }
  };

  // Opdater frivillig (gammel funktion - beholdt for kompatibilitet)
  const updateVolunteer = async () => {
    if (!editingVolunteer) return;
    
    try {
      const endpoint = editingVolunteer.source === 'excel_import'
        ? `${API}/events/${eventId}/imported-volunteers/${editingVolunteer.id}`
        : `${API}/events/${eventId}/volunteers/${editingVolunteer.id}`;
      
      await axios.put(endpoint, {
        name: editingVolunteer.name,
        role: editingVolunteer.role,
        forening: editingVolunteer.forening,
        hold: editingVolunteer.hold,
        phone: editingVolunteer.phone,
        is_leader: editingVolunteer.is_leader || false
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Frivillig opdateret!");
      closeEditDialog();
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering");
    }
  };
  
  // Opdater frivillig MED låse-indstillinger (ny kombineret funktion)
  const updateVolunteerWithLocks = async () => {
    if (!editingVolunteer) return;
    
    try {
      const endpoint = editingVolunteer.source === 'excel_import'
        ? `${API}/events/${eventId}/imported-volunteers/${editingVolunteer.id}`
        : `${API}/events/${eventId}/volunteers/${editingVolunteer.id}`;
      
      await axios.put(endpoint, {
        name: editingVolunteer.name,
        email: editingVolunteer.email,
        role: editingVolunteer.role,
        role_locked: editingVolunteer.role_locked || false,
        forening: editingVolunteer.forening,
        hold: editingVolunteer.hold,
        phone: editingVolunteer.phone,
        is_leader: editingVolunteer.is_leader || false,
        leader_for_main_role_id: editingVolunteer.leader_for_main_role_id || null,
        leader_team_ids: editingVolunteer.leader_team_ids || [],
        // Låse-indstillinger
        main_role_id: editingVolunteer.main_role_id || null,
        main_role_locked: editingVolunteer.main_role_locked || false,
        area_id: editingVolunteer.area_id || null,
        area_locked: editingVolunteer.area_locked || false,
        work_zone_id: editingVolunteer.work_zone_id || null,
        work_zone_locked: editingVolunteer.work_zone_locked || false,
        // Vagter
        shift_ids: editingVolunteer.shift_ids || []
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success("Frivillig opdateret!");
      closeEditDialog();
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved opdatering");
    }
  };

  // Bulk-tildeling
  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) {
      toast.error("Vælg mindst én frivillig");
      return;
    }
    
    // Tjek at mindst ét felt er udfyldt eller en lås-handling er valgt
    const hasAssignment = bulkMainRoleId || bulkShiftIds.length > 0 || bulkAreaId || bulkWorkZoneId;
    const hasLocking = bulkMainRoleLocked || bulkAreaLocked || bulkWorkZoneLocked;
    const hasUnlocking = bulkMainRoleUnlock || bulkAreaUnlock || bulkWorkZoneUnlock;
    
    if (!hasAssignment && !hasLocking && !hasUnlocking) {
      toast.error("Vælg værdier, låsning eller oplåsning");
      return;
    }

    try {
      // Opdater hver frivillig med både tildeling og låse-indstillinger
      let updatedCount = 0;
      
      for (const volId of selectedIds) {
        const volunteer = volunteers.find(v => v.id === volId);
        if (!volunteer) continue;
        
        const endpoint = volunteer.source === 'excel_import'
          ? `${API}/events/${eventId}/imported-volunteers/${volId}`
          : `${API}/events/${eventId}/volunteers/${volId}`;
        
        const updateData = {};
        
        // Tildeling (kun hvis der er valgt en værdi)
        if (bulkMainRoleId) updateData.main_role_id = bulkMainRoleId;
        if (bulkAreaId) updateData.area_id = bulkAreaId;
        if (bulkWorkZoneId) updateData.work_zone_id = bulkWorkZoneId;
        
        // Låse-indstillinger
        if (bulkMainRoleLocked) updateData.main_role_locked = true;
        if (bulkMainRoleUnlock) updateData.main_role_locked = false;
        if (bulkAreaLocked) updateData.area_locked = true;
        if (bulkAreaUnlock) updateData.area_locked = false;
        if (bulkWorkZoneLocked) updateData.work_zone_locked = true;
        if (bulkWorkZoneUnlock) updateData.work_zone_locked = false;
        
        if (Object.keys(updateData).length > 0) {
          await axios.put(endpoint, updateData, { headers: { Authorization: `Bearer ${token}` }});
          updatedCount++;
        }
      }
      
      // Håndter vagter separat via bulk-assign endpoint
      if (bulkShiftIds.length > 0 || bulkMainRoleId) {
        await axios.post(`${API}/events/${eventId}/volunteers/bulk-assign`, {
          volunteer_ids: Array.from(selectedIds),
          main_role_id: bulkMainRoleId || null,
          shift_ids: bulkShiftIds
        }, { headers: { Authorization: `Bearer ${token}` }});
      }
      
      toast.success(`Opdateret ${selectedIds.size} frivillige`);
      setSelectedIds(new Set());
      setSelectAll(false);
      setShowBulkAssign(false);
      // Reset alle bulk felter
      setBulkMainRoleId('');
      setBulkShiftIds([]);
      setBulkMainRoleLocked(false);
      setBulkMainRoleUnlock(false);
      setBulkAreaId('');
      setBulkAreaLocked(false);
      setBulkAreaUnlock(false);
      setBulkWorkZoneId('');
      setBulkWorkZoneLocked(false);
      setBulkWorkZoneUnlock(false);
      loadVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved bulk-opdatering");
    }
  };

  const toggleShiftSelection = (shiftId) => {
    setBulkShiftIds(prev => 
      prev.includes(shiftId) 
        ? prev.filter(id => id !== shiftId)
        : [...prev, shiftId]
    );
  };

  // Filtrér vagter baseret på valgt funktion (for både bulk og enkelt-redigering)
  const getFilteredShifts = () => {
    // Brug funktion fra bulk eller fra den enkelte frivillig
    const currentMainRoleId = selectedIds.size === 1 
      ? editingVolunteer?.main_role_id 
      : bulkMainRoleId;
    
    if (!currentMainRoleId) {
      return shifts; // Vis alle vagter hvis ingen funktion er valgt
    }
    
    const selectedMainRole = mainRoles.find(mr => mr.id === currentMainRoleId);
    const mainRoleShiftIds = selectedMainRole?.shift_ids || [];
    
    if (mainRoleShiftIds.length === 0) {
      return shifts; // Vis alle vagter hvis funktionen ikke har tilknyttede vagter
    }
    
    // Filtrer til kun de vagter der er tilknyttet funktionen
    return shifts.filter(s => mainRoleShiftIds.includes(s.id));
  };
  
  const filteredShifts = getFilteredShifts();

  // Ryd valgte vagter når funktion ændres (hvis de ikke er blandt de filtrerede)
  const handleMainRoleChange = (newMainRoleId) => {
    setBulkMainRoleId(newMainRoleId);
    
    if (newMainRoleId) {
      const selectedMainRole = mainRoles.find(mr => mr.id === newMainRoleId);
      const mainRoleShiftIds = selectedMainRole?.shift_ids || [];
      
      if (mainRoleShiftIds.length > 0) {
        // Fjern valgte vagter der ikke er tilknyttet den nye funktion
        setBulkShiftIds(prev => prev.filter(id => mainRoleShiftIds.includes(id)));
      }
    }
  };

  // Slet frivillig
  const deleteVolunteer = (volunteer) => {
    showConfirm(
      "Slet frivillig?",
      `Er du sikker på du vil slette "${volunteer.name}"?`,
      async () => {
        closeConfirm();
        try {
          const endpoint = volunteer.source === 'excel_import'
            ? `${API}/events/${eventId}/imported-volunteers/${volunteer.id}`
            : `${API}/events/${eventId}/volunteers/${volunteer.id}`;
          
          await axios.delete(endpoint, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success("Frivillig slettet");
          loadVolunteers();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Fejl ved sletning");
        }
      }
    );
  };

  // Hent unikke værdier til filtre
  const uniqueRoles = [...new Set(volunteers.map(v => v.role).filter(Boolean))];
  const uniqueForening = [...new Set(volunteers.map(v => v.forening).filter(Boolean))];

  const getSourceLabel = (source) => {
    switch (source) {
      case 'excel_import': return 'Import';
      case 'excel_legacy': return 'Gammel Excel';
      case 'manual': return 'Manuel';
      default: return source;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'excel_import': return 'bg-green-500/20 text-green-400';
      case 'excel_legacy': return 'bg-yellow-500/20 text-yellow-400';
      case 'manual': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="frivillige-page">
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />

      {/* Låse-indstillinger Dialog */}
      {showLockSettings && lockSettingsVolunteer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg mx-4">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-bold flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Lås-indstillinger
                </h2>
                <p className="text-sm text-muted-foreground">{lockSettingsVolunteer.name} ({lockSettingsVolunteer.email})</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowLockSettings(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Vælg og lås felter på forhånd. Når et felt er låst, kan den frivillige ikke ændre det ved login.
              </p>
              
              {/* Funktion */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Funktion</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lockSettingsVolunteer.main_role_locked}
                      onChange={(e) => setLockSettingsVolunteer({
                        ...lockSettingsVolunteer,
                        main_role_locked: e.target.checked
                      })}
                      className="rounded"
                    />
                    {lockSettingsVolunteer.main_role_locked ? (
                      <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                    )}
                  </label>
                </div>
                <select
                  value={lockSettingsVolunteer.main_role_id}
                  onChange={(e) => setLockSettingsVolunteer({
                    ...lockSettingsVolunteer,
                    main_role_id: e.target.value,
                    // Reset område og arbejdszone når funktion ændres
                    area_id: '',
                    work_zone_id: ''
                  })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                >
                  <option value="">Ikke valgt (frivillig vælger selv)</option>
                  {mainRoles.map(mr => (
                    <option key={mr.id} value={mr.id}>{mr.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Område */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Område</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lockSettingsVolunteer.area_locked}
                      onChange={(e) => setLockSettingsVolunteer({
                        ...lockSettingsVolunteer,
                        area_locked: e.target.checked
                      })}
                      className="rounded"
                    />
                    {lockSettingsVolunteer.area_locked ? (
                      <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                    )}
                  </label>
                </div>
                <select
                  value={lockSettingsVolunteer.area_id}
                  onChange={(e) => setLockSettingsVolunteer({
                    ...lockSettingsVolunteer,
                    area_id: e.target.value,
                    // Reset arbejdszone når område ændres
                    work_zone_id: ''
                  })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                >
                  <option value="">Ikke valgt (frivillig vælger selv)</option>
                  {areas.map(area => (
                    <option key={area.id} value={area.id}>{area.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Arbejdszone */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Arbejdszone</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lockSettingsVolunteer.work_zone_locked}
                      onChange={(e) => setLockSettingsVolunteer({
                        ...lockSettingsVolunteer,
                        work_zone_locked: e.target.checked
                      })}
                      className="rounded"
                    />
                    {lockSettingsVolunteer.work_zone_locked ? (
                      <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                    ) : (
                      <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                    )}
                  </label>
                </div>
                <select
                  value={lockSettingsVolunteer.work_zone_id}
                  onChange={(e) => setLockSettingsVolunteer({
                    ...lockSettingsVolunteer,
                    work_zone_id: e.target.value
                  })}
                  className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                >
                  <option value="">Ikke valgt (frivillig vælger selv)</option>
                  {workZones
                    .filter(wz => !lockSettingsVolunteer.area_id || wz.area_id === lockSettingsVolunteer.area_id)
                    .map(wz => (
                      <option key={wz.id} value={wz.id}>{wz.label || wz.name}</option>
                    ))}
                </select>
              </div>
              
              {/* Info boks */}
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded p-3 text-sm">
                <p className="text-indigo-400">
                  <strong>Tip:</strong> Hvis du låser et felt, kan den frivillige se værdien, men ikke ændre den ved login.
                  Ulåste felter vælger den frivillige selv.
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t border-border flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowLockSettings(false)}>
                Annuller
              </Button>
              <Button onClick={saveLockSettings}>
                Gem indstillinger
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Dialog */}
      {showNoteDialog && noteVolunteer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <h2 className="font-bold">Noter: {noteVolunteer.name}</h2>
                <p className="text-sm text-muted-foreground">{noteVolunteer.email}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowNoteDialog(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-auto p-4 space-y-3">
              {loadingNotes ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                </div>
              ) : notes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Ingen noter endnu</p>
              ) : (
                notes.map(note => (
                  <div key={note.id} className="p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-start justify-between">
                      <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                      {(note.created_by === user?.email || isAdmin) && (
                        <Button variant="ghost" size="sm" onClick={() => deleteNote(note.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {note.created_by} • {new Date(note.created_at).toLocaleString('da-DK')}
                    </p>
                  </div>
                ))
              )}
            </div>
            
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <Input
                  placeholder="Skriv en note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createNote()}
                />
                <Button onClick={createNote} disabled={!newNote.trim()}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kombineret Rediger-dialog (for én ELLER flere frivillige) */}
      {showEditDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div>
                <h2 className="font-bold flex items-center gap-2">
                  <Edit2 className="h-5 w-5" />
                  {selectedIds.size === 1 ? 'Rediger frivillig' : `Rediger ${selectedIds.size} frivillige`}
                </h2>
                {selectedIds.size === 1 && editingVolunteer && (
                  <p className="text-sm text-muted-foreground">{editingVolunteer.email}</p>
                )}
                {selectedIds.size > 1 && (
                  <p className="text-sm text-muted-foreground">Ændringer gælder for alle valgte</p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={closeEditDialog}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* SEKTION: Stamoplysninger - KUN for én frivillig */}
              {selectedIds.size === 1 && editingVolunteer && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold uppercase text-muted-foreground border-b border-border pb-1">Stamoplysninger</h4>
                  
                  {/* Navn */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Navn</label>
                    <input
                      type="text"
                      value={editingVolunteer.name || ''}
                      onChange={(e) => setEditingVolunteer({...editingVolunteer, name: e.target.value})}
                      className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                      data-testid="edit-name-input"
                    />
                  </div>
                  
                  {/* E-mail */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">E-mail</label>
                    <input
                      type="email"
                      value={editingVolunteer.email || ''}
                      onChange={(e) => setEditingVolunteer({...editingVolunteer, email: e.target.value})}
                      className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                      data-testid="edit-email-input"
                    />
                  </div>
                  
                  {/* Telefon */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Telefon</label>
                    <input
                      type="text"
                      value={editingVolunteer.phone || ''}
                      onChange={(e) => setEditingVolunteer({...editingVolunteer, phone: e.target.value})}
                      className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                      data-testid="edit-phone-input"
                    />
                  </div>
                  
                  {/* Forening */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Forening</label>
                    <input
                      type="text"
                      value={editingVolunteer.forening || ''}
                      onChange={(e) => setEditingVolunteer({...editingVolunteer, forening: e.target.value})}
                      className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                      data-testid="edit-forening-input"
                    />
                  </div>
                  
                  {/* Hold (importeret) */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Hold (importeret)</label>
                    <input
                      type="text"
                      value={editingVolunteer.hold || ''}
                      onChange={(e) => setEditingVolunteer({...editingVolunteer, hold: e.target.value})}
                      className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                      data-testid="edit-hold-input"
                    />
                  </div>
                  
                  {/* Er leder */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium">Er leder</label>
                    <div className="h-10 flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingVolunteer.is_leader || false}
                          onChange={(e) => setEditingVolunteer({...editingVolunteer, is_leader: e.target.checked})}
                          className="rounded w-5 h-5"
                          data-testid="edit-leader-checkbox"
                        />
                        <span className={editingVolunteer.is_leader ? "text-yellow-400" : "text-muted-foreground"}>
                          {editingVolunteer.is_leader ? "Ja" : "Nej"}
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Leder for teams - vises kun hvis er leder */}
                  {editingVolunteer.is_leader && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Leder for teams</label>
                      <p className="text-xs text-muted-foreground">Vælg hvilke teams denne person er leder for</p>
                      <div className="max-h-48 overflow-y-auto space-y-1 bg-zinc-800 border border-border rounded p-2">
                        {roles.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Ingen teams oprettet endnu</p>
                        ) : (
                          roles.map(team => (
                            <label key={team.id} className="flex items-center gap-2 py-1 px-2 hover:bg-zinc-700 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(editingVolunteer.leader_team_ids || []).includes(team.id)}
                                onChange={(e) => {
                                  const currentIds = editingVolunteer.leader_team_ids || [];
                                  const newIds = e.target.checked
                                    ? [...currentIds, team.id]
                                    : currentIds.filter(id => id !== team.id);
                                  setEditingVolunteer({...editingVolunteer, leader_team_ids: newIds});
                                }}
                                className="rounded w-4 h-4"
                                data-testid={`leader-team-${team.id}`}
                              />
                              <span className="text-sm">{team.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* SEKTION: Tildeling - For ALLE (én eller flere) - Funktion/Område/Zone SKJULT for ledere */}
              {!(selectedIds.size === 1 && editingVolunteer?.is_leader) && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold uppercase text-muted-foreground border-b border-border pb-1 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Tildeling {selectedIds.size > 1 && `(${selectedIds.size} frivillige)`}
                </h4>
                {selectedIds.size > 1 && (
                  <p className="text-xs text-muted-foreground">
                    Vælg værdier for at tildele dem til alle {selectedIds.size} valgte frivillige.
                  </p>
                )}
                {selectedIds.size === 1 && (
                  <p className="text-xs text-muted-foreground">
                    Vælg og lås felter. Når et felt er låst, kan den frivillige ikke ændre det ved login.
                  </p>
                )}
                
                {/* Funktion + Lås */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Funktion</label>
                    {selectedIds.size === 1 && editingVolunteer && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingVolunteer.main_role_locked || false}
                          onChange={(e) => setEditingVolunteer({...editingVolunteer, main_role_locked: e.target.checked})}
                          className="rounded"
                        />
                        {editingVolunteer.main_role_locked ? (
                          <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                        ) : (
                          <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                        )}
                      </label>
                    )}
                  </div>
                  <select
                    value={selectedIds.size === 1 ? (editingVolunteer?.main_role_id || '') : bulkMainRoleId}
                    onChange={(e) => {
                      if (selectedIds.size === 1) {
                        setEditingVolunteer({
                          ...editingVolunteer, 
                          main_role_id: e.target.value,
                          area_id: '',
                          work_zone_id: ''
                        });
                      } else {
                        setBulkMainRoleId(e.target.value);
                        setBulkAreaId('');
                        setBulkWorkZoneId('');
                      }
                    }}
                    className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="edit-main-role-select"
                  >
                    <option value="">Ikke valgt</option>
                    {mainRoles.map(mr => (
                      <option key={mr.id} value={mr.id}>{mr.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Team + Lås */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Team</label>
                    {selectedIds.size === 1 && editingVolunteer && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingVolunteer.role_locked || false}
                          onChange={(e) => setEditingVolunteer({...editingVolunteer, role_locked: e.target.checked})}
                          className="rounded"
                        />
                        {editingVolunteer.role_locked ? (
                          <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                        ) : (
                          <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                        )}
                      </label>
                    )}
                  </div>
                  <select
                    value={selectedIds.size === 1 ? (editingVolunteer?.role || '') : ''}
                    onChange={(e) => {
                      if (selectedIds.size === 1) {
                        setEditingVolunteer({...editingVolunteer, role: e.target.value});
                      }
                    }}
                    disabled={selectedIds.size > 1}
                    className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm disabled:opacity-50"
                    data-testid="edit-role-select"
                  >
                    <option value="">Ingen rolle</option>
                    {/* Vis teams: matcher funktionen ELLER ikke tildelt en funktion */}
                    {roles
                      .filter(r => {
                        const selectedMainRole = selectedIds.size === 1 
                          ? editingVolunteer?.main_role_id 
                          : bulkMainRoleId;
                        if (!selectedMainRole) return true;
                        return r.main_role_id === selectedMainRole || !r.main_role_id;
                      })
                      .map(r => (
                        <option key={r.id} value={r.name}>{r.name}</option>
                      ))}
                  </select>
                  {selectedIds.size > 1 && (
                    <p className="text-xs text-muted-foreground">Team kan kun ændres for én frivillig ad gangen</p>
                  )}
                </div>
                
                {/* Område + Lås */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Område</label>
                    {selectedIds.size === 1 && editingVolunteer && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingVolunteer.area_locked || false}
                          onChange={(e) => setEditingVolunteer({...editingVolunteer, area_locked: e.target.checked})}
                          className="rounded"
                        />
                        {editingVolunteer.area_locked ? (
                          <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                        ) : (
                          <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                        )}
                      </label>
                    )}
                  </div>
                  <select
                    value={selectedIds.size === 1 ? (editingVolunteer?.area_id || '') : bulkAreaId}
                    onChange={(e) => {
                      if (selectedIds.size === 1) {
                        setEditingVolunteer({
                          ...editingVolunteer, 
                          area_id: e.target.value,
                          work_zone_id: ''
                        });
                      } else {
                        setBulkAreaId(e.target.value);
                        setBulkWorkZoneId('');
                      }
                    }}
                    className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="edit-area-select"
                  >
                    <option value="">Ikke valgt</option>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>{area.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Arbejdszone + Lås */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Arbejdszone</label>
                    {selectedIds.size === 1 && editingVolunteer && (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingVolunteer.work_zone_locked || false}
                          onChange={(e) => setEditingVolunteer({...editingVolunteer, work_zone_locked: e.target.checked})}
                          className="rounded"
                        />
                        {editingVolunteer.work_zone_locked ? (
                          <span className="flex items-center gap-1 text-red-400"><Lock className="h-3 w-3" /> Låst</span>
                        ) : (
                          <span className="flex items-center gap-1 text-muted-foreground"><Unlock className="h-3 w-3" /> Ulåst</span>
                        )}
                      </label>
                    )}
                  </div>
                  <select
                    value={selectedIds.size === 1 ? (editingVolunteer?.work_zone_id || '') : bulkWorkZoneId}
                    onChange={(e) => {
                      if (selectedIds.size === 1) {
                        setEditingVolunteer({...editingVolunteer, work_zone_id: e.target.value});
                      } else {
                        setBulkWorkZoneId(e.target.value);
                      }
                    }}
                    className="w-full h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="edit-workzone-select"
                  >
                    <option value="">Ikke valgt</option>
                    {workZones
                      .filter(wz => {
                        const areaId = selectedIds.size === 1 ? editingVolunteer?.area_id : bulkAreaId;
                        return !areaId || wz.area_id === areaId;
                      })
                      .map(wz => (
                        <option key={wz.id} value={wz.id}>{wz.label || wz.name}</option>
                      ))}
                  </select>
                </div>
              </div>
              )} {/* End conditional: Funktion/Område/Zone skjult for ledere */}
              
              {/* SEKTION: Vagter - ALTID synlig (også for ledere) */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold uppercase text-muted-foreground border-b border-border pb-1 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Vagter
                </h4>
                <div className="space-y-2">
                  <div className="bg-zinc-800 border border-border rounded p-3 max-h-40 overflow-y-auto">
                    {filteredShifts.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        {shifts.length === 0 
                          ? 'Ingen vagter oprettet endnu' 
                          : 'Ingen vagter for valgt funktion'}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {filteredShifts.map(shift => {
                          const isSelected = selectedIds.size === 1 
                            ? (editingVolunteer?.shift_ids || []).includes(shift.id)
                            : bulkShiftIds.includes(shift.id);
                          return (
                            <label key={shift.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (selectedIds.size === 1) {
                                    const currentIds = editingVolunteer?.shift_ids || [];
                                    const newIds = currentIds.includes(shift.id)
                                      ? currentIds.filter(id => id !== shift.id)
                                      : [...currentIds, shift.id];
                                    setEditingVolunteer({...editingVolunteer, shift_ids: newIds});
                                  } else {
                                    toggleShiftSelection(shift.id);
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="text-sm">
                                {shift.date ? `${shift.date} ` : ''}{shift.start_time}-{shift.end_time} ({shift.name})
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-border flex justify-end gap-2 sticky bottom-0 bg-card">
              <Button variant="ghost" onClick={closeEditDialog}>
                Annuller
              </Button>
              <Button 
                onClick={selectedIds.size === 1 ? updateVolunteerWithLocks : handleBulkAssign} 
                data-testid="save-edit-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {selectedIds.size === 1 ? 'Gem ændringer' : `Opdater ${selectedIds.size} frivillige`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Frivillige</h1>
                <p className="text-sm text-muted-foreground">{eventName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Bulk action knapper */}
              {selectedIds.size > 0 && (
                <>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      // Åbn den kombinerede dialog for både 1 og flere frivillige
                      if (selectedIds.size === 1) {
                        const selectedId = Array.from(selectedIds)[0];
                        const volunteer = volunteers.find(v => v.id === selectedId);
                        if (volunteer) {
                          openEditDialog(volunteer);
                        }
                      } else {
                        // Flere valgt - reset bulk states og åbn dialog
                        setBulkMainRoleId('');
                        setBulkAreaId('');
                        setBulkWorkZoneId('');
                        setBulkShiftIds([]);
                        setShowEditDialog(true);
                      }
                    }}
                    data-testid="bulk-assign-btn"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Rediger ({selectedIds.size})
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={bulkDelete}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Slet ({selectedIds.size})
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={loadVolunteers}
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToExcel}
                data-testid="export-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Eksporter
              </Button>
              {isAdmin && !isArchived && (
                <Button
                  size="sm"
                  onClick={() => setShowAddForm(true)}
                  data-testid="add-volunteer-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Tilføj
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tilføj frivillig formular - vises øverst når aktiv */}
      {showAddForm && (
        <div className="container mx-auto px-4 pt-4">
          <div className="p-4 bg-card border border-primary/30 rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Tilføj ny frivillig</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Navn *"
                value={newVolunteer.name}
                onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})}
                data-testid="new-volunteer-name"
              />
              <Input
                placeholder="Email *"
                type="email"
                value={newVolunteer.email}
                onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})}
                data-testid="new-volunteer-email"
              />
              <select
                value={newVolunteer.role}
                onChange={(e) => setNewVolunteer({...newVolunteer, role: e.target.value})}
                className="px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                data-testid="new-volunteer-role"
              >
                <option value="">Vælg team...</option>
                {roles.map(r => (
                  <option key={r.id || r.name} value={r.id}>{r.name}</option>
                ))}
              </select>
              <Input
                placeholder="Forening"
                value={newVolunteer.forening}
                onChange={(e) => setNewVolunteer({...newVolunteer, forening: e.target.value})}
              />
              <Input
                placeholder="Hold"
                value={newVolunteer.hold}
                onChange={(e) => setNewVolunteer({...newVolunteer, hold: e.target.value})}
              />
              <Input
                placeholder="Telefon"
                value={newVolunteer.phone}
                onChange={(e) => setNewVolunteer({...newVolunteer, phone: e.target.value})}
              />
            </div>
            <div className="mt-4 flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input 
                  type="checkbox" 
                  checked={newVolunteer.is_leader}
                  onChange={(e) => setNewVolunteer({...newVolunteer, is_leader: e.target.checked})}
                  className="rounded"
                />
                Kan logge ind som leder
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuller</Button>
                <Button onClick={addVolunteer} data-testid="save-new-volunteer-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Tilføj
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 py-6 flex flex-col h-[calc(100vh-73px)]">
        {isArchived && <ArchivedBanner />}
        {/* Sticky sektion med statistik + filtre + kolonner */}
        <div className="flex-shrink-0">
          {/* Statistik-kort */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-4">
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Crown className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.leaders || 0}</p>
                  <p className="text-xs text-muted-foreground">Ledere</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Briefcase className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{mainRoles.length}</p>
                  <p className="text-xs text-muted-foreground">Funktioner</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Building className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{Object.keys(stats.byForening).length}</p>
                  <p className="text-xs text-muted-foreground">Foreninger</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <MapPin className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{areas.length}</p>
                  <p className="text-xs text-muted-foreground">Områder</p>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-card border border-border rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <Map className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{workZones.length}</p>
                  <p className="text-xs text-muted-foreground">Arbejdszoner</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Team-fordeling badges (flyttet fra bunden) */}
          {!loading && Object.keys(stats.byRole).length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              <span className="text-xs text-muted-foreground mr-1 self-center">Teams:</span>
              {Object.entries(stats.byRole)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([role, count]) => (
                  <button
                    key={role}
                    onClick={() => setFilterRole(filterRole === role ? "" : role)}
                    className={`px-2 py-0.5 rounded-full text-xs transition-colors ${
                      filterRole === role 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted/50 hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    {role}: {count}
                  </button>
                ))}
              {Object.keys(stats.byRole).length > 10 && (
                <span className="text-xs text-muted-foreground self-center">+{Object.keys(stats.byRole).length - 10} flere</span>
              )}
            </div>
          )}
          
          {/* Søg og filtrer - fast i toppen */}
          <div className="space-y-4 mb-4">
            <div className="flex gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Søg efter navn, email, forening..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              {/* Hold-filter for ledere */}
              {isLeader && !isAdmin && mainRoles.length > 0 && (
                <select
                  value={selectedTeamFilter}
                  onChange={(e) => handleTeamFilterChange(e.target.value)}
                  className="h-10 px-3 bg-zinc-800 border border-border rounded text-white text-sm min-w-[180px]"
                  data-testid="team-filter-select"
                >
                  <option value="all">Alle hold</option>
                  {mainRoles.map(mr => (
                    <option key={mr.id} value={mr.id}>{mr.name}</option>
                  ))}
                </select>
              )}
              
              <Button
                variant={showFilters ? "default" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtre
                {(filterRole || filterSource || filterForening || filterNoMainRole || filterNoRole || filterHasNotes) && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary rounded-full">
                    {[filterRole, filterSource, filterForening, filterNoMainRole, filterNoRole, filterHasNotes].filter(Boolean).length}
                  </span>
                )}
              </Button>
              
              <Button
                variant={showColumnSettings ? "default" : "outline"}
                onClick={() => setShowColumnSettings(!showColumnSettings)}
                data-testid="toggle-columns-btn"
              >
                <Columns className="h-4 w-4 mr-2" />
                Kolonner
              </Button>
            </div>
            
            {/* Kolonne-indstillinger */}
            {showColumnSettings && (
              <div className="p-4 bg-card border border-border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-sm">Vælg synlige kolonner</h3>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setVisibleColumns(['name', 'email', 'funktion', 'team', 'is_leader', 'forening', 'source', 'notes'])}
                  >
                    Nulstil
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'name', label: 'Navn' },
                    { id: 'email', label: 'Email' },
                    { id: 'phone', label: 'Telefon' },
                    { id: 'funktion', label: 'Funktion' },
                    { id: 'team', label: 'Team' },
                    { id: 'is_leader', label: 'Leder' },
                    { id: 'forening', label: 'Forening' },
                    { id: 'hold', label: 'Hold' },
                    { id: 'source', label: 'Kilde' },
                    { id: 'notes', label: 'Noter' },
                    { id: 'area', label: 'Område' },
                    { id: 'workzone', label: 'Arbejdszone' },
                    { id: 'shifts', label: 'Vagter' }
                  ].map(col => (
                    <button
                      key={col.id}
                      onClick={() => {
                        if (visibleColumns.includes(col.id)) {
                          setVisibleColumns(visibleColumns.filter(c => c !== col.id));
                        } else {
                          setVisibleColumns([...visibleColumns, col.id]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all ${
                        visibleColumns.includes(col.id)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {visibleColumns.includes(col.id) ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <EyeOff className="h-3 w-3" />
                      )}
                      {col.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {showFilters && (
              <div className="p-4 bg-card border border-border rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {/* Funktion filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Funktion</label>
                    <select
                      value={filterMainRole}
                      onChange={(e) => setFilterMainRole(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-main-role"
                    >
                      <option value="">Alle funktioner</option>
                      {mainRoles.map(mr => (
                        <option key={mr.id} value={mr.id}>{mr.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Team</label>
                    <select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-role"
                    >
                      <option value="">Alle roller</option>
                      {uniqueRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Forening</label>
                    <select
                      value={filterForening}
                      onChange={(e) => setFilterForening(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-forening"
                    >
                      <option value="">Alle foreninger</option>
                      {Object.keys(stats.byForening).sort().map(f => (
                        <option key={f} value={f}>{f} ({stats.byForening[f]})</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Kilde</label>
                    <select
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-source"
                    >
                      <option value="">Alle kilder</option>
                      <option value="excel_import">Import</option>
                      <option value="manual">Manuel</option>
                      <option value="excel_legacy">Gammel Excel</option>
                    </select>
                  </div>
                  
                  {/* Område filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Område</label>
                    <select
                      value={filterArea}
                      onChange={(e) => {
                        setFilterArea(e.target.value);
                        setFilterWorkZone(''); // Reset arbejdszone når område ændres
                      }}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-area"
                    >
                      <option value="">Alle områder</option>
                      {areas.map(area => (
                        <option key={area.id} value={area.id}>{area.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Arbejdszone filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Arbejdszone</label>
                    <select
                      value={filterWorkZone}
                      onChange={(e) => setFilterWorkZone(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-workzone"
                    >
                      <option value="">Alle arbejdszoner</option>
                      {workZones
                        .filter(wz => !filterArea || wz.area_id === filterArea)
                        .map(wz => (
                          <option key={wz.id} value={wz.id}>{wz.label || wz.name}</option>
                        ))}
                    </select>
                  </div>
                  
                  {/* Vagt filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Vagt</label>
                    <select
                      value={filterShift}
                      onChange={(e) => setFilterShift(e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-border rounded-md text-white"
                      data-testid="filter-shift"
                    >
                      <option value="">Alle vagter</option>
                      {shifts.map(shift => (
                        <option key={shift.id} value={shift.id}>
                          {shift.date ? `${shift.date} ` : ''}{shift.start_time}-{shift.end_time} ({shift.name})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="mt-4 flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterNoMainRole}
                      onChange={(e) => setFilterNoMainRole(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Uden funktion</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterNoRole}
                      onChange={(e) => setFilterNoRole(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Uden rolle</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterHasNotes}
                      onChange={(e) => setFilterHasNotes(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Har noter</span>
                  </label>
                </div>
                
                {(filterRole || filterSource || filterForening || filterNoMainRole || filterNoRole || filterHasNotes || filterMainRole || filterArea || filterWorkZone || filterShift) && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setFilterRole("");
                        setFilterSource("");
                        setFilterForening("");
                        setFilterNoMainRole(false);
                        setFilterNoRole(false);
                        setFilterHasNotes(false);
                        setFilterMainRole("");
                        setFilterArea("");
                        setFilterWorkZone("");
                        setFilterShift("");
                      }}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Ryd filtre
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Resultat-info */}
          <div className="text-sm text-muted-foreground">
            Viser {filteredVolunteers.length} af {volunteers.length} frivillige
          </div>
        </div>
        
        {/* Scrollbar tabel-container */}
        <div className="flex-1 overflow-y-auto min-h-0 mt-2">

        {/* Bulk-tildelings panel - STICKY */}
        {showBulkAssign && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t-2 border-primary shadow-2xl shadow-black/50 max-h-[70vh] overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Edit2 className="h-5 w-5 text-primary" />
                  Rediger {selectedIds.size} frivillige
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setShowBulkAssign(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Oversigt over valgte frivilliges nuværende tildelinger */}
              {(() => {
                const selectedVolunteers = volunteers.filter(v => selectedIds.has(v.id));
                const mainRoleCounts = {};
                const areaCounts = {};
                const workZoneCounts = {};
                let lockedMainRole = 0, lockedArea = 0, lockedWorkZone = 0;
                
                selectedVolunteers.forEach(v => {
                  if (v.main_role_id) {
                    const name = mainRoles.find(mr => mr.id === v.main_role_id)?.name || 'Ukendt';
                    mainRoleCounts[name] = (mainRoleCounts[name] || 0) + 1;
                  }
                  if (v.area_id) {
                    const name = areas.find(a => a.id === v.area_id)?.name || 'Ukendt';
                    areaCounts[name] = (areaCounts[name] || 0) + 1;
                  }
                  if (v.work_zone_id) {
                    const name = workZones.find(wz => wz.id === v.work_zone_id)?.label || workZones.find(wz => wz.id === v.work_zone_id)?.name || 'Ukendt';
                    workZoneCounts[name] = (workZoneCounts[name] || 0) + 1;
                  }
                  if (v.main_role_locked) lockedMainRole++;
                  if (v.area_locked) lockedArea++;
                  if (v.work_zone_locked) lockedWorkZone++;
                });
                
                const hasExisting = Object.keys(mainRoleCounts).length > 0 || Object.keys(areaCounts).length > 0 || Object.keys(workZoneCounts).length > 0;
                const hasLocks = lockedMainRole > 0 || lockedArea > 0 || lockedWorkZone > 0;
                
                return (hasExisting || hasLocks) && (
                  <div className="mb-4 p-3 bg-muted/30 border border-border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-amber-400" />
                      <span className="text-sm font-medium">Nuværende tildelinger for valgte frivillige:</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground">Funktioner:</span>
                        {Object.keys(mainRoleCounts).length === 0 ? (
                          <span className="ml-2 text-muted-foreground">Ingen</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(mainRoleCounts).map(([name, count]) => (
                              <span key={name} className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400">
                                {name}: {count}
                              </span>
                            ))}
                          </div>
                        )}
                        {lockedMainRole > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-red-400">
                            <Lock className="h-3 w-3" />
                            {lockedMainRole} låst
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Områder:</span>
                        {Object.keys(areaCounts).length === 0 ? (
                          <span className="ml-2 text-muted-foreground">Ingen</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(areaCounts).map(([name, count]) => (
                              <span key={name} className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                {name}: {count}
                              </span>
                            ))}
                          </div>
                        )}
                        {lockedArea > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-red-400">
                            <Lock className="h-3 w-3" />
                            {lockedArea} låst
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Arbejdszoner:</span>
                        {Object.keys(workZoneCounts).length === 0 ? (
                          <span className="ml-2 text-muted-foreground">Ingen</span>
                        ) : (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {Object.entries(workZoneCounts).map(([name, count]) => (
                              <span key={name} className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-400">
                                {name}: {count}
                              </span>
                            ))}
                          </div>
                        )}
                        {lockedWorkZone > 0 && (
                          <div className="mt-1 flex items-center gap-1 text-red-400">
                            <Lock className="h-3 w-3" />
                            {lockedWorkZone} låst
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Funktion + Lås/Lås op */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium">Funktion</label>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås feltet">
                        <input
                          type="checkbox"
                          checked={bulkMainRoleLocked}
                          onChange={(e) => {
                            setBulkMainRoleLocked(e.target.checked);
                            if (e.target.checked) setBulkMainRoleUnlock(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Lock className="h-3 w-3 text-red-400" />
                      </label>
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås op">
                        <input
                          type="checkbox"
                          checked={bulkMainRoleUnlock}
                          onChange={(e) => {
                            setBulkMainRoleUnlock(e.target.checked);
                            if (e.target.checked) setBulkMainRoleLocked(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Unlock className="h-3 w-3 text-green-400" />
                      </label>
                    </div>
                  </div>
                  <select
                    value={bulkMainRoleId}
                    onChange={(e) => handleMainRoleChange(e.target.value)}
                    className="w-full px-2 py-1.5 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="bulk-main-role-select"
                  >
                    <option value="">Behold nuværende</option>
                    {mainRoles.map(mr => (
                      <option key={mr.id} value={mr.id}>
                        {mr.name}
                        {(mr.shift_ids?.length > 0) ? ` (${mr.shift_ids.length} vagter)` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Område + Lås/Lås op */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium">Område</label>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås feltet">
                        <input
                          type="checkbox"
                          checked={bulkAreaLocked}
                          onChange={(e) => {
                            setBulkAreaLocked(e.target.checked);
                            if (e.target.checked) setBulkAreaUnlock(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Lock className="h-3 w-3 text-red-400" />
                      </label>
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås op">
                        <input
                          type="checkbox"
                          checked={bulkAreaUnlock}
                          onChange={(e) => {
                            setBulkAreaUnlock(e.target.checked);
                            if (e.target.checked) setBulkAreaLocked(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Unlock className="h-3 w-3 text-green-400" />
                      </label>
                    </div>
                  </div>
                  <select
                    value={bulkAreaId}
                    onChange={(e) => {
                      setBulkAreaId(e.target.value);
                      setBulkWorkZoneId('');
                    }}
                    className="w-full px-2 py-1.5 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="bulk-area-select"
                  >
                    <option value="">Behold nuværende</option>
                    {areas.map(area => (
                      <option key={area.id} value={area.id}>{area.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Arbejdszone + Lås/Lås op */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium">Arbejdszone</label>
                    <div className="flex gap-2">
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås feltet">
                        <input
                          type="checkbox"
                          checked={bulkWorkZoneLocked}
                          onChange={(e) => {
                            setBulkWorkZoneLocked(e.target.checked);
                            if (e.target.checked) setBulkWorkZoneUnlock(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Lock className="h-3 w-3 text-red-400" />
                      </label>
                      <label className="flex items-center gap-1 text-xs cursor-pointer" title="Lås op">
                        <input
                          type="checkbox"
                          checked={bulkWorkZoneUnlock}
                          onChange={(e) => {
                            setBulkWorkZoneUnlock(e.target.checked);
                            if (e.target.checked) setBulkWorkZoneLocked(false);
                          }}
                          className="rounded w-3 h-3"
                        />
                        <Unlock className="h-3 w-3 text-green-400" />
                      </label>
                    </div>
                  </div>
                  <select
                    value={bulkWorkZoneId}
                    onChange={(e) => setBulkWorkZoneId(e.target.value)}
                    className="w-full px-2 py-1.5 bg-zinc-800 border border-border rounded text-white text-sm"
                    data-testid="bulk-workzone-select"
                  >
                    <option value="">Behold nuværende</option>
                    {workZones
                      .filter(wz => !bulkAreaId || wz.area_id === bulkAreaId)
                      .map(wz => (
                        <option key={wz.id} value={wz.id}>{wz.label || wz.name}</option>
                      ))}
                  </select>
                </div>
                
                {/* Vagter */}
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Vagter ({bulkShiftIds.length} valgt)
                    {bulkMainRoleId && filteredShifts.length !== shifts.length && (
                      <span className="text-xs text-indigo-400 ml-2">
                        (filtreret til {filteredShifts.length} af {shifts.length})
                      </span>
                    )}
                  </label>
                  <div className="max-h-24 overflow-y-auto space-y-1 p-2 bg-muted/30 rounded text-xs">
                    {filteredShifts.length === 0 ? (
                      <p className="text-muted-foreground">
                        {shifts.length === 0 
                          ? "Ingen vagter oprettet" 
                          : "Ingen vagter tilknyttet denne funktion"}
                      </p>
                    ) : (
                      filteredShifts.map(shift => (
                        <label key={shift.id} className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bulkShiftIds.includes(shift.id)}
                            onChange={() => toggleShiftSelection(shift.id)}
                            className="w-3 h-3 rounded"
                          />
                          <span>{shift.start_time}-{shift.end_time} ({shift.name})</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
              
              {/* Forklaring */}
              <div className="mt-3 text-xs text-muted-foreground flex items-center gap-4">
                <span className="flex items-center gap-1"><Lock className="h-3 w-3 text-red-400" /> = Lås felt (frivillig kan ikke ændre)</span>
                <span className="flex items-center gap-1"><Unlock className="h-3 w-3 text-green-400" /> = Lås op (fjern eksisterende lås)</span>
              </div>
              
              <div className="flex gap-2 mt-3">
                <Button onClick={handleBulkAssign} size="sm" data-testid="bulk-assign-save-btn">
                  <Check className="h-4 w-4 mr-1" />
                  Gem ændringer
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowBulkAssign(false)}>
                  Annuller
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Frivillig-liste */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Indlæser frivillige...</p>
          </div>
        ) : filteredVolunteers.length === 0 ? (
          <div className="text-center py-12 bg-card/30 rounded-lg border border-dashed border-border">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Ingen frivillige fundet</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || filterRole || filterSource || filterForening 
                ? "Prøv at ændre dine søgekriterier" 
                : "Upload en Excel-fil i Stamdata for at tilføje frivillige"}
            </p>
            {isAdmin && !searchTerm && (
              <Button onClick={() => navigate("/stamdata")}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Gå til Stamdata
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-zinc-900 sticky top-0 z-10 border-b border-border">
                  <tr>
                    {isAdmin && (
                      <th className="p-3 w-10">
                        <button onClick={toggleSelectAll} className="p-1 hover:bg-muted rounded">
                          {selectAll ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                        </button>
                      </th>
                    )}
                    {visibleColumns.includes('name') && <th className="text-left p-3 font-medium">Navn</th>}
                    {visibleColumns.includes('email') && <th className="text-left p-3 font-medium">Email</th>}
                    {visibleColumns.includes('is_leader') && <th className="text-left p-3 font-medium">Leder</th>}
                    {visibleColumns.includes('funktion') && <th className="text-left p-3 font-medium">Funktion</th>}
                    {visibleColumns.includes('team') && <th className="text-left p-3 font-medium">Team</th>}
                    {visibleColumns.includes('phone') && <th className="text-left p-3 font-medium">Telefon</th>}
                    {visibleColumns.includes('forening') && <th className="text-left p-3 font-medium">Forening</th>}
                    {visibleColumns.includes('hold') && <th className="text-left p-3 font-medium">Hold</th>}
                    {visibleColumns.includes('area') && <th className="text-left p-3 font-medium">Område</th>}
                    {visibleColumns.includes('workzone') && <th className="text-left p-3 font-medium">Arbejdszone</th>}
                    {visibleColumns.includes('shifts') && <th className="text-left p-3 font-medium">Vagter</th>}
                    {visibleColumns.includes('source') && <th className="text-left p-3 font-medium">Kilde</th>}
                    {visibleColumns.includes('notes') && <th className="text-left p-3 font-medium">Noter</th>}
                    {isAdmin && <th className="text-left p-3 font-medium">Slet</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredVolunteers.map((v, idx) => {
                    const roleLower = (v.role || '').toLowerCase();
                    const isVolAdmin = roleLower === 'admin' || roleLower === 'administrator';
                    const isVolLeader = v.is_leader || ['leder', 'leader', 'teamleder', 'sektionsleder'].includes(roleLower);
                    
                    return (
                    <tr 
                      key={v.id || idx} 
                      className={`border-t border-border hover:bg-muted/30 transition-colors ${
                        isVolAdmin ? 'bg-red-500/5' : isVolLeader ? 'bg-yellow-500/5' : ''
                      }`}
                    >
                          {isAdmin && (
                            <td className="p-3">
                              {v.can_edit !== false && (
                                <button 
                                  onClick={() => toggleSelect(v.id)} 
                                  className="p-1 hover:bg-muted rounded"
                                >
                                  {selectedIds.has(v.id) ? (
                                    <CheckSquare className="h-4 w-4 text-primary" />
                                  ) : (
                                    <Square className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('name') && (
                            <td className="p-3 font-medium">
                              <div className="flex items-center gap-2">
                                {isVolAdmin && <Shield className="h-4 w-4 text-red-500" />}
                                {isVolLeader && !isVolAdmin && <Crown className="h-4 w-4 text-yellow-500" />}
                                {v.name}
                                {v.display_name && v.display_name !== v.name && (
                                  <span className="text-xs text-muted-foreground">({v.display_name})</span>
                                )}
                              </div>
                            </td>
                          )}
                          {visibleColumns.includes('email') && (
                            <td className="p-3">
                              <a href={`mailto:${v.email}`} className="text-primary hover:underline flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {v.email}
                              </a>
                            </td>
                          )}
                          {visibleColumns.includes('is_leader') && (
                            <td className="p-3">
                              {v.is_leader ? (
                                <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">Ja</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('funktion') && (
                            <td className="p-3">
                              {v.main_role_id ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-400">
                                    {mainRoles.find(mr => mr.id === v.main_role_id)?.name || 'Ukendt'}
                                  </span>
                                  {v.main_role_locked && <Lock className="h-3 w-3 text-red-400" title="Låst" />}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('team') && (
                            <td className="p-3">
                              {(() => {
                                const teamName = getTeamName(v.role);
                                return (teamName && teamName.toLowerCase() !== 'frivillig') 
                                  ? teamName 
                                  : <span className="text-muted-foreground">-</span>;
                              })()}
                            </td>
                          )}
                          {visibleColumns.includes('phone') && (
                            <td className="p-3">
                              {v.phone ? (
                                <a href={`tel:${v.phone}`} className="text-muted-foreground hover:text-primary flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {v.phone}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('forening') && (
                            <td className="p-3">{v.forening || <span className="text-muted-foreground">-</span>}</td>
                          )}
                          {visibleColumns.includes('hold') && (
                            <td className="p-3">{v.hold || <span className="text-muted-foreground">-</span>}</td>
                          )}
                          {visibleColumns.includes('area') && (
                            <td className="p-3">
                              {v.area_id ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">
                                    {areas.find(a => a.id === v.area_id)?.name || '-'}
                                  </span>
                                  {v.area_locked && <Lock className="h-3 w-3 text-red-400" title="Låst" />}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('workzone') && (
                            <td className="p-3">
                              {v.work_zone_id ? (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs px-2 py-1 rounded bg-teal-500/20 text-teal-400">
                                    {workZones.find(wz => wz.id === v.work_zone_id)?.label || workZones.find(wz => wz.id === v.work_zone_id)?.name || '-'}
                                  </span>
                                  {v.work_zone_locked && <Lock className="h-3 w-3 text-red-400" title="Låst" />}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('shifts') && (
                            <td className="p-3">
                              {v.shift_ids && v.shift_ids.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {v.shift_ids.slice(0, 3).map(shiftId => {
                                    const shift = shifts.find(s => s.id === shiftId);
                                    return shift ? (
                                      <span key={shiftId} className="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                                        {shift.start_time?.substring(0, 5) || '?'}
                                      </span>
                                    ) : null;
                                  })}
                                  {v.shift_ids.length > 3 && (
                                    <span className="text-xs text-muted-foreground">+{v.shift_ids.length - 3}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">-</span>
                              )}
                            </td>
                          )}
                          {visibleColumns.includes('source') && (
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded ${getSourceColor(v.source)}`}>
                                {getSourceLabel(v.source)}
                              </span>
                            </td>
                          )}
                          {visibleColumns.includes('notes') && (
                            <td className="p-3">
                              {isLeader && (
                                <Button
                                  size="sm"
                                  variant={v.has_notes ? "default" : "ghost"}
                                  onClick={() => openNotes(v)}
                                  className={v.has_notes ? "bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30" : ""}
                                >
                                  <MessageSquare className="h-3 w-3" />
                                  {v.has_notes && <span className="ml-1 text-xs">!</span>}
                                </Button>
                              )}
                            </td>
                          )}
                          {isAdmin && (
                            <td className="p-3">
                              {v.can_edit !== false && (
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => deleteVolunteer(v)}
                                    className="text-destructive hover:bg-destructive/10"
                                    title="Slet"
                                    data-testid={`delete-volunteer-${v.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </td>
                          )}
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Spacer for sticky panel */}
        {showBulkAssign && <div className="h-48" />}
        
        </div>{/* Slut på scrollbar container */}
        <PoweredByFooter />
      </main>
    </div>
  );
}
